import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { listKontaktLieferanten, type Geschaeftspartner } from "@/lib/kontakte";
import {
  linkEingangsrechnungArtikelLieferant,
  type EingangsrechnungArtikelLine,
} from "@/lib/invoiceMail";

function rawLieferantName(row: EingangsrechnungArtikelLine): string {
  return (row.lieferant_name || row.candidate_from_name || "").trim();
}

export function LinkLieferantDialog({
  open,
  lines,
  onClose,
  onLinked,
}: {
  open: boolean;
  lines: EingangsrechnungArtikelLine[];
  onClose: () => void;
  onLinked: () => void;
}) {
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState<Geschaeftspartner[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedGpId, setSelectedGpId] = useState<number | null>(null);
  const [applyToSameName, setApplyToSameName] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const distinctRawNames = useMemo(() => {
    const names = new Set(lines.map(rawLieferantName).filter(Boolean));
    return [...names];
  }, [lines]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelectedGpId(null);
    setApplyToSameName(distinctRawNames.length === 1);
    const hint = distinctRawNames[0] || "";
    setSearchQ(hint);
  }, [open, lines, distinctRawNames]);

  useEffect(() => {
    if (!open) return;
    const q = searchQ.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearchLoading(true);
      void listKontaktLieferanten({ q, limit: 20 })
        .then((res) => setResults(res.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [open, searchQ]);

  async function handleSubmit() {
    if (!selectedGpId) {
      setError("Bitte einen Lieferanten auswählen.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await linkEingangsrechnungArtikelLieferant({
        ids: lines.map((l) => l.id),
        lieferant_geschaeftspartner_id: selectedGpId,
        apply_to_same_lieferant_name: applyToSameName,
      });
      const name = res.lieferant_firmenname || "Lieferant";
      toast.success(
        res.updated === 1
          ? `1 Position mit „${name}“ verknüpft.`
          : `${res.updated ?? 0} Positionen mit „${name}“ verknüpft.`,
      );
      onLinked();
      onClose();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Verknüpfung fehlgeschlagen.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Lieferant verknüpfen" size="lg">
      <div className="space-y-4 text-sm">
        <p className="text-label-secondary">
          {lines.length === 1
            ? `Position: ${lines[0]?.bezeichnung || "—"}`
            : `${lines.length} Positionen ausgewählt`}
        </p>

        {distinctRawNames.length > 0 ? (
          <div className="rounded border border-separator bg-card/50 px-3 py-2 text-xs text-label-secondary">
            Rechnungs-Lieferant (automatisch):{" "}
            <span className="font-medium text-label-primary">
              {distinctRawNames.join(" · ")}
            </span>
          </div>
        ) : (
          <div className="rounded border border-separator bg-card/50 px-3 py-2 text-xs text-label-secondary">
            Kein Lieferantenname auf der Rechnung erkannt — bitte manuell aus Stammdaten wählen.
          </div>
        )}

        <div>
          <label className="mb-1 block font-medium">Lieferant aus Stammdaten suchen</label>
          <input
            className="w-full rounded border border-separator bg-card px-3 py-2 text-sm"
            placeholder="Firmenname…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
          {searchLoading ? <div className="mt-2 text-xs text-label-secondary">Suche…</div> : null}
          {results.length > 0 ? (
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded border border-separator p-2">
              {results.map((gp) => (
                <li key={gp.id}>
                  <button
                    type="button"
                    className={`w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accent/10 ${
                      selectedGpId === gp.id ? "bg-accent/15 ring-1 ring-accent/40" : ""
                    }`}
                    onClick={() => setSelectedGpId(gp.id)}
                  >
                    <span className="font-medium text-label-primary">{gp.firmenname}</span>
                    {gp.ort ? (
                      <span className="ml-2 text-label-secondary">{gp.ort}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : searchQ.trim().length >= 2 && !searchLoading ? (
            <p className="mt-2 text-xs text-label-secondary">Kein Lieferant gefunden.</p>
          ) : null}
        </div>

        {distinctRawNames.length === 1 ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={applyToSameName}
              onChange={(e) => setApplyToSameName(e.target.checked)}
            />
            <span>
              Auch alle anderen Positionen mit Rechnungs-Lieferant „
              {distinctRawNames[0]}“ verknüpfen
            </span>
          </label>
        ) : distinctRawNames.length > 1 ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={applyToSameName}
              onChange={(e) => setApplyToSameName(e.target.checked)}
            />
            <span>
              Auch alle Positionen mit gleichem Rechnungs-Lieferantennamen verknüpfen (aus der
              Auswahl)
            </span>
          </label>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={loading || !selectedGpId}>
            {loading ? <Spinner className="h-3.5 w-3.5" /> : "Verknüpfen"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
